import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Change, Project, User } from '../../models';
import {
    loadChange,
    loadChanges,
    loadProjects,
    loadUsers,
    resetChanges,
    toChangeStep,
    useAppDispatch,
    useAppSelector,
    RootState,
} from '../../redux';
import { Changes } from '../../services';
import {
    Ability, Alert, Button, ChangeModal, ChangesOverviewTable, Confirm, Icon,
    MergeChangesModal, MultiSelect, Select,
} from '../../components';
import type { Value } from '../../components/Select';
import {
    parseSortParams,
    sortChangesByColumn,
    type OverviewColumnId,
    type SortDirection,
} from '../../components/ChangesOverviewTable';
import SearchAdvancedFilters, {
    SEARCH_STATUSES,
} from '../../components/SearchModal/SearchAdvancedFilters';
import TriageModifyModal from '../Triage/TriageModifyModal';
import {
    advancedFieldFiltersFromParams,
    appendAdvancedFieldFiltersToParams,
    filtersToQuery,
    hasActiveAdvancedFieldFilters,
    type AdvancedFieldFilters,
} from '../../utils/searchFilters';
import { matchesRequester } from '../../utils/changeRequester';
import ChangesOverviewTableSkeleton from '../../components/ChangesOverviewTable/ChangesOverviewTableSkeleton';

const OVERVIEW_STATUSES = SEARCH_STATUSES;

/** Statuses that imply historical / closed cards — skip notfinished when selected. */
const HISTORICAL_STATUSES = [
    Change.STATUS_ESTIMATED,
    Change.STATUS_PENDING_PLANIFICATION,
    Change.STATUS_PLANNED,
    Change.STATUS_DEPLOYED,
    Change.STATUS_FINISHED,
];

const emptyAdvancedFilters: AdvancedFieldFilters = {};

function canBulkAccept(change: Change): boolean {
    return change.progress?.status === Change.STATUS_PENDING_TRIAGE;
}

function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, ' ');
}

function changeMatchesSearch(change: Change, query: string, lang: string): boolean {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return true;

    const title = (change.localizedTitle(lang) || '').toLowerCase();
    const description = stripHtml(change.localizedDescription(lang) || '').toLowerCase();
    const slug = String(change.slug ?? '').toLowerCase();
    const slugWithPrefix = `f${slug}`;
    const ticketNumber = String(change.ticket_number ?? '').toLowerCase();
    return title.includes(trimmed)
        || description.includes(trimmed)
        || slug.includes(trimmed)
        || slugWithPrefix.includes(trimmed)
        || ticketNumber.includes(trimmed)
        || matchesRequester(change, trimmed);
}

function memberToUser(member: User | string, usersById: Record<string, User>): User | null {
    if (typeof member === 'string') {
        return usersById[member] || null;
    }
    if (member?._id) {
        return member;
    }
    return null;
}

function collectAssigneesFromProjects(projectList: Project[], allUsers: User[]): User[] {
    const usersById: Record<string, User> = {};
    allUsers.forEach((user: User) => { usersById[user._id] = user; });

    const byId: Record<string, User> = {};
    projectList.forEach((project: Project) => {
        project.members?.forEach((member: User | string) => {
            const user = memberToUser(member, usersById);
            if (user) {
                byId[user._id] = user;
            }
        });
    });

    return Object.values(byId).sort((a, b) => a.fullname().localeCompare(b.fullname()));
}

function hasUsefulFilter(args: {
    statusFilter: Value[];
    projectFilter: Value;
    typeFilter: Value;
    assigneeFilter: Value;
    textSearch: string;
    advancedFilters: AdvancedFieldFilters;
}): boolean {
    return args.statusFilter.length > 0
        || args.projectFilter !== 'all'
        || args.typeFilter !== 'all'
        || args.assigneeFilter !== 'all'
        || Boolean(args.textSearch.trim())
        || hasActiveAdvancedFieldFilters(args.advancedFilters);
}

function shouldUseNotFinished(args: {
    statusFilter: Value[];
    textSearch: string;
    advancedFilters: AdvancedFieldFilters;
}): boolean {
    if (args.statusFilter.some((status) => HISTORICAL_STATUSES.includes(String(status)))) {
        return false;
    }
    if (args.textSearch.trim()) {
        return false;
    }
    if (hasActiveAdvancedFieldFilters(args.advancedFilters)) {
        return false;
    }
    return true;
}

/** Parse `status=assigned,inProgress` (multi) into a Value[]. */
function parseStatusParam(raw: string | null): Value[] {
    if (!raw || raw === 'all') {
        return [];
    }
    return raw
        .split(',')
        .map((item) => item.trim())
        .filter((item) => OVERVIEW_STATUSES.includes(item));
}

function ChangesOverviewView() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialStatus = parseStatusParam(searchParams.get('status'));
    const initialProject = searchParams.get('project') || 'all';
    const initialType = searchParams.get('type') || 'all';
    const initialAssignee = searchParams.get('assignee') || 'all';
    const initialSort = parseSortParams(searchParams.get('sort'), searchParams.get('dir'));
    const initialSearch = searchParams.get('q') || '';
    const initialAdvanced = advancedFieldFiltersFromParams(searchParams);
    const initialAdvancedOpen = searchParams.get('advanced') === '1'
        || hasActiveAdvancedFieldFilters(initialAdvanced);

    const [statusFilter, setStatusFilter] = useState<Value[]>(initialStatus);
    const [projectFilter, setProjectFilter] = useState<Value>(initialProject);
    const [typeFilter, setTypeFilter] = useState<Value>(initialType);
    const [assigneeFilter, setAssigneeFilter] = useState<Value>(initialAssignee);
    const [sortBy, setSortBy] = useState<OverviewColumnId>(initialSort.sortBy);
    const [sortDir, setSortDir] = useState<SortDirection>(initialSort.sortDir);
    const [textSearch, setTextSearch] = useState(initialSearch);
    const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFieldFilters>(initialAdvanced);
    const [advancedOpen, setAdvancedOpen] = useState(initialAdvancedOpen);
    const [modalChangeId, setModalChangeId] = useState<string | undefined>();
    const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
    const [processingBulk, setProcessingBulk] = useState(false);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [modifyTargetIds, setModifyTargetIds] = useState<string[]>([]);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetIds, setMergeTargetIds] = useState<string[]>([]);
    const [bulkFeedback, setBulkFeedback] = useState<{
        title: string;
        color: 'success' | 'failure' | 'warning';
    } | null>(null);

    const { changes, loading, change: detailChange } = useAppSelector((state: RootState) => state.changes);
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { users } = useAppSelector((state: RootState) => state.users);
    const { user } = useAppSelector((state: RootState) => state.auth);

    const isClient = user?.role === User.ROLE_CLIENT;
    const accessibleProjects = projects || [];
    const advancedFiltersActive = hasActiveAdvancedFieldFilters(advancedFilters);

    const filtersReady = hasUsefulFilter({
        statusFilter,
        projectFilter,
        typeFilter,
        assigneeFilter,
        textSearch: debouncedSearch,
        advancedFilters,
    });

    const reloadChanges = () => {
        if (!hasUsefulFilter({
            statusFilter,
            projectFilter,
            typeFilter,
            assigneeFilter,
            textSearch: debouncedSearch,
            advancedFilters,
        })) {
            dispatch(resetChanges());
            return;
        }

        const params: AnyObj = {
            ...filtersToQuery(advancedFilters),
            lang: i18n.language,
        };
        if (shouldUseNotFinished({
            statusFilter,
            textSearch: debouncedSearch,
            advancedFilters,
        })) {
            params.notfinished = true;
        }
        if (statusFilter.length > 0) {
            params['progress.status'] = statusFilter.join(',');
        }
        if (projectFilter !== 'all') {
            params.project = projectFilter;
        }
        if (typeFilter !== 'all') {
            params.types = typeFilter;
        }
        if (assigneeFilter === 'unassigned') {
            params.unassigned = true;
        } else if (assigneeFilter !== 'all') {
            params.assignee = assigneeFilter;
        }
        if (debouncedSearch.trim()) {
            params.query = debouncedSearch.trim();
        }
        dispatch(loadChanges(params));
    };

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(textSearch), 300);
        return () => window.clearTimeout(timer);
    }, [textSearch]);

    useEffect(() => {
        dispatch(loadProjects());
        if (!isClient) {
            dispatch(loadUsers());
        }
    }, [dispatch, isClient]);

    useEffect(() => {
        reloadChanges();
    }, [dispatch, statusFilter, projectFilter, typeFilter, assigneeFilter, debouncedSearch, advancedFilters, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const params = new URLSearchParams();
        if (statusFilter.length > 0) params.set('status', statusFilter.join(','));
        if (projectFilter !== 'all') params.set('project', String(projectFilter));
        if (typeFilter !== 'all') params.set('type', String(typeFilter));
        if (assigneeFilter !== 'all') params.set('assignee', String(assigneeFilter));
        if (sortBy !== 'status' || sortDir !== 'asc') {
            params.set('sort', sortBy);
            params.set('dir', sortDir);
        }
        if (textSearch.trim()) params.set('q', textSearch.trim());
        appendAdvancedFieldFiltersToParams(params, advancedFilters);
        if (advancedOpen) params.set('advanced', '1');
        setSearchParams(params, { replace: true });
    }, [
        statusFilter, projectFilter, typeFilter, assigneeFilter, sortBy, sortDir, textSearch,
        advancedFilters, advancedOpen, setSearchParams,
    ]);

    useEffect(() => {
        setSelectedChangeIds([]);
    }, [statusFilter, projectFilter, typeFilter, assigneeFilter, sortBy, sortDir, debouncedSearch, advancedFilters]);

    const selectableProjects = useMemo(() => {
        return accessibleProjects.slice().sort((a: Project, b: Project) => a.name.localeCompare(b.name));
    }, [accessibleProjects]);

    const clientProjectIds = useMemo(() => {
        return new Set(accessibleProjects.map((project: Project) => project._id));
    }, [accessibleProjects]);

    const selectableTypes = useMemo(() => Change.types(), []);

    const selectableAssignees = useMemo(() => {
        if (isClient) {
            return collectAssigneesFromProjects(accessibleProjects, users || []);
        }

        const byId: Record<string, User> = {};
        (users || []).forEach((item: User) => { byId[item._id] = item; });
        return Object.values(byId).sort((a, b) => a.fullname().localeCompare(b.fullname()));
    }, [isClient, accessibleProjects, users]);

    useEffect(() => {
        if (assigneeFilter === 'all' || assigneeFilter === 'unassigned') return;
        const isAllowed = selectableAssignees.some((item: User) => item._id === assigneeFilter);
        if (!isAllowed && selectableAssignees.length > 0) {
            setAssigneeFilter('all');
        }
    }, [assigneeFilter, selectableAssignees]);

    const filteredChanges = useMemo(() => {
        if (!filtersReady) return [];

        const filtered = (changes || [])
            .filter((item: Change) => item.progress?.status !== Change.STATUS_DISCARDED)
            .filter((item: Change) => item.progress?.status !== Change.STATUS_SPAM_SUSPECT)
            .filter((item: Change) => !isClient || clientProjectIds.has(item.project?._id))
            .filter((item: Change) => (
                statusFilter.length === 0 || statusFilter.includes(item.progress?.status)
            ))
            .filter((item: Change) => projectFilter === 'all' || item.project?._id === projectFilter)
            .filter((item: Change) => typeFilter === 'all' || item.type === typeFilter)
            .filter((item: Change) => {
                if (assigneeFilter === 'all') return true;
                if (assigneeFilter === 'unassigned') return !item.assignee?._id;
                return item.assignee?._id === assigneeFilter;
            })
            .filter((item: Change) => changeMatchesSearch(item, textSearch, i18n.language))
            .filter((item: Change) => (
                !advancedFilters.requestorEmail?.trim()
                || matchesRequester(item, advancedFilters.requestorEmail)
            ));

        return sortChangesByColumn(filtered, sortBy, sortDir, i18n.language);
    }, [
        filtersReady, changes, isClient, clientProjectIds, statusFilter, projectFilter,
        typeFilter, assigneeFilter, textSearch, advancedFilters.requestorEmail,
        sortBy, sortDir, i18n.language,
    ]);

    const selectedChanges = useMemo(() => {
        return filteredChanges.filter((item: Change) => selectedChangeIds.includes(item._id));
    }, [filteredChanges, selectedChangeIds]);

    const acceptableSelected = useMemo(() => selectedChanges.filter(canBulkAccept), [selectedChanges]);

    const selectedCount = selectedChangeIds.length;
    const allSelected = filteredChanges.length > 0 && selectedCount === filteredChanges.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const bulkDeleteConfirmTitle = selectedCount > 1
        ? t('changes_overview.confirm_delete_many', { count: selectedCount })
        : t('changes_overview.confirm_delete_one');

    const toggleChangeSelection = (changeId: string) => {
        setSelectedChangeIds((prev) => (
            prev.includes(changeId) ? prev.filter((id) => id !== changeId) : [...prev, changeId]
        ));
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedChangeIds([]);
            return;
        }
        setSelectedChangeIds(filteredChanges.map((item: Change) => item._id));
    };

    const closeChangeModal = () => {
        setModalChangeId(undefined);
        dispatch(resetChanges(true));
    };

    const openChangeModal = (item: Change) => {
        if (!item.project?._id || !item._id) return;
        setModalChangeId(item._id);
        dispatch(loadChange(item._id));
    };

    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh the list.
        closeChangeModal();
        reloadChanges();
    };

    const reportBulkResult = (successCount: number, errorCount: number, requestedCount: number) => {
        if (errorCount === 0) {
            setBulkFeedback({
                color: 'success',
                title: t('priorities.bulk_success', { count: successCount }) as string,
            });
            return;
        }
        if (successCount > 0) {
            setBulkFeedback({
                color: 'warning',
                title: t('priorities.bulk_partial', {
                    success: successCount,
                    failed: errorCount,
                }) as string,
            });
            return;
        }
        setBulkFeedback({
            color: 'failure',
            title: t('priorities.bulk_error', { count: requestedCount }) as string,
        });
    };

    const openModifyModal = (ids: string[]) => {
        if (!ids.length) return;
        setModifyTargetIds(ids);
        setModifyModalOpen(true);
    };

    const openMergeModal = (ids: string[]) => {
        if (ids.length < 2) return;
        setMergeTargetIds(ids);
        setMergeModalOpen(true);
    };

    const applyModify = async (payload: { assigneeId?: string; projectId?: string }) => {
        const requestedCount = modifyTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'modify',
                changeIds: modifyTargetIds,
                assignee: payload.assigneeId,
                projectId: payload.projectId,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setModifyModalOpen(false);
            setModifyTargetIds([]);
            setSelectedChangeIds([]);
            reloadChanges();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const applyMerge = async (target: Change) => {
        if (!target?._id || !mergeTargetIds.length) return;
        const requestedCount = mergeTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            await Changes.merge(target._id, mergeTargetIds);
            reportBulkResult(requestedCount, 0, requestedCount);
            setMergeModalOpen(false);
            setMergeTargetIds([]);
            setSelectedChangeIds([]);
            if (modalChangeId && mergeTargetIds.includes(modalChangeId)) {
                closeChangeModal();
            }
            reloadChanges();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const resolveCards = async (ids: string[]) => {
        if (!ids.length) return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'resolve',
                changeIds: ids,
                assignee: user?._id,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            reloadChanges();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const discardCards = async (ids: string[]) => {
        if (!ids.length) return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'discard',
                changeIds: ids,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            setShowBulkDeleteConfirm(false);
            reloadChanges();
        } catch {
            reportBulkResult(0, requestedCount, requestedCount);
            setShowBulkDeleteConfirm(false);
        } finally {
            setProcessingBulk(false);
        }
    };

    const performBulkAccept = async () => {
        if (!acceptableSelected.length) return;
        setProcessingBulk(true);
        setBulkFeedback(null);
        for (const change of acceptableSelected) {
            await new Promise<void>((resolve) => {
                dispatch(toChangeStep(change._id, Change.STATUS_PENDING_ESTIMATE, {}, () => resolve()));
            });
        }
        setSelectedChangeIds([]);
        setProcessingBulk(false);
        setBulkFeedback({
            color: 'success',
            title: t('priorities.bulk_success', { count: acceptableSelected.length }) as string,
        });
        reloadChanges();
    };

    const pageTitle = statusFilter.length === 1
        ? t(`changes.progress_status_type.${statusFilter[0]}`)
        : t('changes_overview.title');

    document.title = pageTitle;

    return (
        <Ability can="change:read" redirect="/">
            <div className="ChangesOverview">
                <header className="border-b bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 lg:px-8">
                    <h2 className="flex items-center text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        <Icon type="list" size={7} className="mr-1.5" />
                        {pageTitle}
                        {filtersReady && (
                            <span className="ml-2 text-lg font-normal text-gray-500 dark:text-gray-400">
                                ({filteredChanges.length})
                            </span>
                        )}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('changes_overview.subtitle')}
                    </p>
                </header>

                <main className="m-5">
                    <div className="relative mb-4">
                        <input
                            type="search"
                            value={textSearch}
                            onChange={(e) => setTextSearch(e.target.value)}
                            placeholder={t('changes_overview.search') as string}
                            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2.5 pr-10 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
                        />
                        <Icon type="search" className="pointer-events-none absolute top-2.5 right-3" size={5} color="gray-400" />
                    </div>

                    <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
                        <MultiSelect
                            onChange={setStatusFilter}
                            defaultValue={statusFilter}
                            placeholder={t('changes_overview.filters.all_statuses') as string}
                            options={OVERVIEW_STATUSES.map((status) => ({
                                value: status,
                                label: t(`changes.progress_status_type.${status}`) || status,
                            }))}
                        />
                        <Select
                            onChange={setProjectFilter}
                            defaultValue={projectFilter}
                            placeholder={t('priorities.filters.project') as string}
                            options={[
                                { value: 'all', label: t('priorities.filters.all_projects') || '' },
                                ...selectableProjects.map((item: Project) => ({ value: item._id, label: item.name })),
                            ]}
                        />
                        <Select
                            onChange={setTypeFilter}
                            defaultValue={typeFilter}
                            placeholder={t('priorities.filters.type') as string}
                            options={[
                                { value: 'all', label: t('priorities.filters.all_types') || '' },
                                ...selectableTypes.map((item: string) => ({
                                    value: item,
                                    label: t(`projects.${item}`) || item,
                                })),
                            ]}
                        />
                        <Select
                            onChange={setAssigneeFilter}
                            defaultValue={assigneeFilter}
                            placeholder={t('priorities.filters.assignee') as string}
                            options={[
                                { value: 'all', label: t('priorities.filters.all_assignees') || '' },
                                { value: 'unassigned', label: t('priorities.filters.unassigned') || '' },
                                ...selectableAssignees.map((item: User) => ({
                                    value: item._id,
                                    label: item.fullname() || item.email,
                                })),
                            ]}
                        />
                    </div>

                    <button
                        type="button"
                        className="mb-4 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        onClick={() => setAdvancedOpen((open) => !open)}
                    >
                        <Icon type={advancedOpen ? 'up' : 'down'} size={4} />
                        {t('search.advanced_filters')}
                        {advancedFiltersActive && !advancedOpen && (
                            <span className="ml-1 rounded bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 text-xs text-blue-700 dark:text-blue-300">
                                {t('search.filters_active')}
                            </span>
                        )}
                    </button>

                    {advancedOpen && (
                        <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                            <SearchAdvancedFilters
                                filters={advancedFilters}
                                onChange={(next) => setAdvancedFilters(next)}
                                showCoreFilters={false}
                            />
                            {advancedFiltersActive && (
                                <button
                                    type="button"
                                    className="mt-3 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                    onClick={() => setAdvancedFilters(emptyAdvancedFilters)}
                                >
                                    {t('changes_overview.clear_advanced_filters')}
                                </button>
                            )}
                        </div>
                    )}

                    {!filtersReady && (
                        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            {t('changes_overview.need_filter')}
                        </p>
                    )}

                    {filtersReady && selectedCount > 0 && (
                        <div className="mb-4 rounded-lg border bg-white dark:bg-gray-800 p-3 shadow-sm">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold select-none">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                        onChange={toggleSelectAll}
                                        className="h-4 w-4 cursor-pointer"
                                    />
                                    {t('priorities.bulk_actions')}
                                    <span className="ml-1 text-blue-600">({selectedCount})</span>
                                    {processingBulk && (
                                        <span className="ml-2 inline-flex items-center gap-1 font-normal text-gray-500 dark:text-gray-400">
                                            <Icon type="loading" size={4} color="gray-500" />
                                            {t('priorities.bulk_processing')}
                                        </span>
                                    )}
                                </label>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        title={t('priorities.modify') as string}
                                        icon="edit"
                                        small
                                        color="primary"
                                        onClick={() => { if (!processingBulk) openModifyModal(selectedChangeIds); }}
                                        className={processingBulk ? 'opacity-50' : ''}
                                    />
                                    <Button
                                        title={t('priorities.bulk_merge') as string}
                                        icon="merge"
                                        small
                                        color="primary"
                                        onClick={() => { if (!processingBulk) openMergeModal(selectedChangeIds); }}
                                        className={processingBulk || selectedCount < 2 ? 'opacity-50' : ''}
                                    />
                                    <Button
                                        title={t('priorities.resolve') as string}
                                        icon="check"
                                        small
                                        color="success"
                                        onClick={() => { if (!processingBulk) resolveCards(selectedChangeIds); }}
                                        className={processingBulk ? 'opacity-50' : ''}
                                    />
                                    <Button
                                        title={t('priorities.discard') as string}
                                        icon="delete"
                                        small
                                        color="danger"
                                        onClick={() => {
                                            if (!processingBulk && selectedCount) {
                                                setShowBulkDeleteConfirm(true);
                                            }
                                        }}
                                        className={processingBulk ? 'opacity-50' : ''}
                                    />
                                    {acceptableSelected.length > 0 && (
                                        <Button
                                            title={`${t('changes_overview.bulk.accept')} (${acceptableSelected.length})`}
                                            icon="check"
                                            small
                                            color="success"
                                            onClick={() => {
                                                if (!processingBulk) performBulkAccept();
                                            }}
                                            className={processingBulk ? 'opacity-50' : ''}
                                        />
                                    )}
                                </div>
                            </div>
                            <Confirm
                                visible={showBulkDeleteConfirm}
                                onConfirm={() => discardCards(selectedChangeIds)}
                                onCancel={() => setShowBulkDeleteConfirm(false)}
                                title={bulkDeleteConfirmTitle || ''}
                            />
                        </div>
                    )}

                    {bulkFeedback && (
                        <div className="mb-3">
                            <Alert
                                title={bulkFeedback.title}
                                color={bulkFeedback.color}
                                icon={bulkFeedback.color === 'success' ? 'check' : 'warning'}
                            />
                        </div>
                    )}

                    {filtersReady && loading === 'pending' && (
                        <ChangesOverviewTableSkeleton rows={8} />
                    )}

                    {filtersReady && loading !== 'pending' && filteredChanges.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            {t('changes_overview.empty')}
                        </p>
                    )}

                    {filtersReady && loading !== 'pending' && filteredChanges.length > 0 && (
                        <ChangesOverviewTable
                            changes={filteredChanges}
                            selectedChangeIds={selectedChangeIds}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            onSortChange={(nextSort, nextDir) => {
                                setSortBy(nextSort);
                                setSortDir(nextDir);
                            }}
                            onToggleSelect={toggleChangeSelection}
                            onToggleSelectAll={toggleSelectAll}
                            onRowClick={openChangeModal}
                        />
                    )}
                </main>

                {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                    <ChangeModal
                        project={detailChange.project}
                        change={detailChange}
                        show={true}
                        showProjectName={true}
                        onClose={closeChangeModal}
                        onRemove={onRemoveCard}
                    />
                )}

                <TriageModifyModal
                    show={modifyModalOpen}
                    count={modifyTargetIds.length}
                    assignees={selectableAssignees}
                    projects={selectableProjects}
                    processing={processingBulk}
                    onClose={() => {
                        setModifyModalOpen(false);
                        setModifyTargetIds([]);
                    }}
                    onApply={applyModify}
                />

                <MergeChangesModal
                    show={mergeModalOpen}
                    sources={filteredChanges.filter((item: Change) => mergeTargetIds.includes(item._id))}
                    processing={processingBulk}
                    defaultProjectId={filteredChanges.find((item: Change) => mergeTargetIds.includes(item._id))?.project?._id}
                    onClose={() => {
                        setMergeModalOpen(false);
                        setMergeTargetIds([]);
                    }}
                    onApply={applyMerge}
                />
            </div>
        </Ability>
    );
}

export default ChangesOverviewView;
